/**
 * What is derived from a world, and derived on every step.
 *
 * Ports, bodies and rope segments are never stored. They come from the parts
 * and links the student built, which is the only way the drawing and the
 * physics can be guaranteed to agree — there is one source, and it is the
 * arrangement itself.
 */
import { defOf, Freedom, Support } from './registry';
import { taut } from './tangent';
import { ACROSS, DOWN, dot, len, sub, unit } from './vec';
import { Body, Bond, Part, Port, PortRef, Rope, Vec2, World } from './types';

export const partById = (w: World, id: string): Part | undefined => w.parts.find(p => p.id === id);

export const ropesOf = (w: World): Rope[] => w.links.filter((l): l is Rope => l.type === 'rope');
export const bondsOf = (w: World): Bond[] => w.links.filter((l): l is Bond => l.type === 'bond');

export function portsOf(w: World, part: Part): Port[] {
    return defOf(part).ports(part as never, w);
}

/**
 * Which way a part's own width runs: level, unless it is standing on a face.
 *
 * A block on a ramp lies ALONG the ramp, and that is a fact about the physics
 * before it is one about the drawing. Measured as an upright box, a block on a
 * 30° face was held off it by `½·width·sin θ + ½·height·cos θ` — an upright
 * box's shadow on the slope — where a block lying on the face is held off by
 * half its height and nothing else. So the engine seated it at one height, the
 * drawing stood it upright at another, and neither matched a block resting on
 * a slope.
 *
 * Pointed rightward (x ≥ 0) whichever way the face was written, so a block on
 * the left slope of a wedge is not turned upside down to lie on it.
 */
export function frameOf(w: World, part: Part): Vec2 {
    const on = supportOf(w, part);
    if (!on) return ACROSS;
    const a = on.axes[0];
    return a.x < 0 ? { x: -a.x, y: -a.y } : a;
}

/** How far a part reaches from its centre in a given direction — measured in
 *  the part's own frame, so a block lying on a slope is as wide along it and
 *  as tall across it as it is standing on the floor. */
export function extentAlong(w: World, part: Part, dir: Vec2): number {
    const e = defOf(part).extent(part as never);
    const across = frameOf(w, part);
    const along = dot(dir, across);
    const down = -dir.x * across.y + dir.y * across.x;
    return Math.abs(along) * e.x + Math.abs(down) * e.y;
}

/** The face a part is resting on, if it rests on one. */
export function supportOf(w: World, part: Part): Support | undefined {
    const on = part.support ? partById(w, part.support) : undefined;
    return on ? defOf(on).supports?.(on as never, w, part.face) : undefined;
}

/**
 * Which ways a part may travel.
 *
 * Its kind's answer — unless it is resting on something, in which case the
 * thing it rests on decides. That one indirection is why `weight` has never
 * heard of a table: a block on a ramp goes down the ramp because the RAMP says
 * so, and adding a curved chute later changes nothing outside its own file.
 */
export function freedomOf(w: World, part: Part): Freedom {
    // A pin beats everything: it is the answer to "which ways may this travel"
    // and the answer is none.
    if (part.pinned) return { axes: [] };
    const on = supportOf(w, part);
    return on ? { axes: on.axes } : defOf(part).freedom(part as never, w);
}

/** Is anything on this bench being held? */
export const anyPinned = (w: World): boolean => w.parts.some(p => p.pinned);

/**
 * The same bench with every pin let go — what happens when you press play.
 *
 * A new world rather than a flag the solver checks, because "held" and "let
 * go" are two different arrangements and the bench genuinely has two answers.
 * Returning the same object when there is nothing to release keeps the run
 * from being rebuilt on every render.
 */
export const released = (w: World): World =>
    (anyPinned(w)
        ? { ...w, parts: w.parts.map(p => (p.pinned ? { ...p, pinned: undefined } : p)) }
        : w);

/**
 * What to call one join point, for a reader: `P₂_L`, `m₁`, `H₂`.
 *
 * The part's name, plus the kind's own suffix for that port when it has more
 * than one place a rope can go. A weight has one hook and does not need
 * telling apart; a pulley has a left rim, a right rim and a centre, and which
 * of the three a rope is threaded over is the whole shape of the arrangement.
 */
export function portLabel(w: World, ref: PortRef): string {
    const part = partById(w, ref.partId);
    if (!part) return ref.partId;
    const def = defOf(part);
    const name = def.name(part as never, w);
    const port = portsOf(w, part).find(p => p.id === ref.portId);
    const suffix = port && def.portName?.(port);
    return suffix ? `${name}_${suffix}` : name;
}

export function allPorts(w: World): Port[] {
    return w.parts.flatMap(p => portsOf(w, p));
}

export function portAt(w: World, ref: PortRef): Port | undefined {
    const part = partById(w, ref.partId);
    return part && portsOf(w, part).find(p => p.id === ref.portId);
}

/**
 * Where a rope actually touches a part at one of its stops.
 *
 * `toward` is the neighbour it is heading for, which is what tells a sheave
 * which way round it is being used when the student has not said.
 */
export function contactPoint(w: World, ref: PortRef, toward: Vec2): Vec2 {
    const part = partById(w, ref.partId);
    const port = part && portsOf(w, part).find(p => p.id === ref.portId);
    if (!part || !port) return { x: 0, y: 0 };
    return defOf(part).ropeContact(part as never, port, toward).at;
}

export interface Segment {
    ropeId: string;
    /** Index of its first stop in the rope's path. */
    index: number;
    a: PortRef;
    b: PortRef;
    /** Contact points, in bench coordinates. */
    pa: Vec2;
    pb: Vec2;
    /** Unit vector from a to b, and the distance. */
    dir: Vec2;
    length: number;
    /**
     * The arc this run turns through at the stop it ENDS on — FOR DRAWING.
     *
     * Not rope. A pulley has no size in the model (§5.2 of the target doc), so
     * this length is not part of `runLength` and never reaches a constraint
     * row. What it is for is the picture: a rope meeting a point is not a
     * pulley, so the scene still draws the line going round the rim, and this
     * says how big that rim is and how far round the rope goes.
     *
     * The turn is the angle between the run arriving and the run leaving —
     * half a turn where it comes down one side and goes up the other, nothing
     * at all where it barely deflects.
     */
    wrap?: {
        radius: number;
        /** How far round it turns, in radians. */
        angle: number;
        /** `radius · angle` — rope, and counted as such by `runLength`. */
        length: number;
        /** Where the rope lands and where it leaves, as angles about the axle
         *  in bench coordinates. The arc between them IS the wrap. */
        from: number;
        to: number;
        /** Which way round, `+1` for increasing angle. */
        sense: number;
    };
    /** Which SPAN of its rope this run belongs to — see `spansOf`. Zero for
     *  every run of a rope nothing along it can grip, which is every rope on
     *  an ideal bench. */
    span: number;
}

/**
 * A stretch of rope along which the tension cannot vary.
 *
 * This is the thing that actually has a tension, and naming it is the point.
 * "One rope, one tension" is not a fact about ropes — it is what follows when
 * nothing along the rope can hold a difference, which is what an ideal sheave
 * being massless and free-turning buys you. Ask each stop whether it can
 * (`PartKind.gripsRope`) and the rope divides itself wherever the answer is
 * yes.
 *
 * Today nothing says yes, so every rope is exactly one span and the numbers
 * are identical to what they were. What changes is that the assumption is now
 * written down in the place it applies, and a sheave with inertia becomes one
 * flag rather than a second mechanism.
 */
export interface Span {
    ropeId: string;
    /** Its position along the rope, from the end the path starts at. */
    index: number;
    /** The multiplier's name in a `Solution` — this span's tension. */
    id: string;
    segments: Segment[];
    /** How long this stretch is right now: its STRAIGHT runs, and nothing
     *  else. A wheel takes no rope — see `runLength`. */
    now: number;
    /** How long it was laid out as. */
    was: number;
}

/** What a span's tension is called in a `Solution`. */
export const spanId = (ropeId: string, index: number): string => `rope:${ropeId}:${index}`;

/**
 * What each tension is CALLED: `T`, or `T₁ … Tₙ` when there is more than one.
 *
 * A reader checking arithmetic needs a name per unknown, and the name has to
 * be the same one everywhere — in the variables list, on the rope, and in
 * every free-body diagram that force appears in. One function, so it is.
 *
 * Numbered per SPAN and not per rope. A span is the thing that has a tension
 * (see `spansOf`); the scene used to number by rope, which agrees only while
 * every rope is one span and would quietly start lying the day one is not.
 *
 * Two ropes both labelled "the rope" — which is what the bench calls them
 * unless the reader renames one — gave two variable rows reading `T in the
 * rope`, with different values and no way to tell which was which.
 */
const SUB = ['₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
export function tensionNames(w: World): Map<string, string> {
    const spans = spansOf(w);
    return new Map(spans.map((s, i) => [s.id, spans.length > 1 ? `T${SUB[i] ?? i + 1}` : 'T']));
}

/**
 * What to call the push of a SURFACE on each part resting on one: N, or N₁, N₂
 * when there is more than one — the textbook's normal force, named the way the
 * tensions are.
 *
 * Every part standing on a face counts, held or not, so a block keeps its
 * number between the setup diagram (where a pin may be holding it) and the run
 * (where the pin is out). Keyed by the part.
 */
export function normalNames(w: World): Map<string, string> {
    const resting = w.parts.filter(p => !!p.support);
    return new Map(resting.map((p, i) => [p.id, resting.length > 1 ? `N${SUB[i] ?? i + 1}` : 'N']));
}

/** The tension acting in this run, from a solved arrangement. Undefined when
 *  its span is slack, which is a stronger statement than zero. */
export const tensionOf = (force: Record<string, number>, seg: Segment): number | undefined =>
    force[spanId(seg.ropeId, seg.span)];

/**
 * The port a run actually leaves from, which is not always the one named.
 *
 * A rope running OVER a sheave touches both rims: it arrives on one side and
 * leaves on the other, because that is what running over a sheave means. The
 * path names each stop once — the side the student picked — so the departing
 * run takes the mirror of it. Naming both would make the student say twice
 * what the wheel says once.
 */
export function effectivePort(w: World, ref: PortRef, leaving: boolean): PortRef {
    if (!leaving) return ref;
    const part = partById(w, ref.partId);
    if (!part) return ref;
    const ports = portsOf(w, part);
    const here = ports.find(p => p.id === ref.portId);
    if (!here || here.role !== 'guide') return ref;
    // The rim pointing the opposite way from the centre: left for right, and
    // just as much top for bottom.
    const out = outward(part, here);
    const other = ports.find(p => p.role === 'guide' && dot(outward(part, p), out) < -0.999);
    return other ? { partId: ref.partId, portId: other.id } : ref;
}

/** Which way a port faces: from its part's centre out to it. Zero for a port
 *  at the centre, which faces nowhere. */
export function outward(part: Part, port: Port): Vec2 {
    const d = sub(port.at, part.at);
    return len(d) < 1e-12 ? { x: 0, y: 0 } : unit(d);
}

/**
 * Every rope's runs, with their current geometry.
 *
 * ## Three passes, and why
 *
 * A run is TANGENT to the wheels at its ends (`tangent.ts`), so where it
 * touches depends on which way it points, and which way it points depends on
 * where it touches. That is not circular, it is one equation — but it needs to
 * know which SIDE of the rope each wheel's centre is on, and that is a
 * discrete choice which has to be made before the equation can be written.
 *
 *   1. runs between CENTRES, which is the arrangement to within a radius and
 *      is all that is needed to see which way the rope bends;
 *   2. the side of each wheel, from that bend — a rope hugs a pulley, so the
 *      axle is on the inside of the turn, and it stays on that same hand for
 *      both runs, which is what wrapping means;
 *   3. the runs again, tangent this time.
 *
 * No iteration: step 3 does not change step 2's answer except in arrangements
 * where the rope barely deflects at all, and there the side it picks makes no
 * difference to anything.
 */
export function segmentsOf(w: World): Segment[] {
    /** The wheel radius at a stop, or zero for anything that is not one. */
    const wheelAt = (ref: PortRef): number => {
        const part = partById(w, ref.partId);
        const port = part && portsOf(w, part).find(p => p.id === ref.portId);
        if (!part || !port) return 0;
        return defOf(part).ropeContact(part as never, port, part.at).wrapRadius ?? 0;
    };

    /**
     * Where a run is HELD at a stop.
     *
     * A wheel turns the rope about its centre, and that is what `taut` wants.
     * Anything else holds the rope WHERE IT IS TIED — the point the reader
     * clicked, which the part's own `ropeContact` names.
     *
     * It used to be the part's centre, with one exception for a block's
     * becket. The centre happens to BE the tie-off point for a weight, a hook,
     * a rope end and an axle, so for years nothing showed it — but a table's
     * or a wedge's corners are nowhere near its centre, which is the middle of
     * its base. A pulley tied to a wedge's apex had its rope drawn, and
     * measured, straight down through the wedge to the floor. A spring
     * balance's hooks are 0.22 m either side of its middle, and its ropes now
     * end on them as well.
     */
    const heldAt = (ref: PortRef, toward: Vec2): Vec2 => {
        const part = partById(w, ref.partId);
        if (!part || wheelAt(ref)) return part?.at ?? { x: 0, y: 0 };
        return contactPoint(w, ref, toward);
    };

    const out: Segment[] = [];
    ropesOf(w).forEach(rope => {
        const stops: PortRef[] = rope.path.map((ref, i) => effectivePort(w, ref, i > 0 && i + 1 < rope.path.length));
        const centre = (ref: PortRef): Vec2 => partById(w, ref.partId)?.at ?? { x: 0, y: 0 };

        // ── 1. between centres, to see the shape of it
        const rough: Vec2[] = [];
        for (let i = 0; i + 1 < stops.length; i++) rough.push(unit(sub(centre(stops[i + 1]), centre(stops[i]))));

        /* ── 2. which hand each wheel is on
         *
         * At a stop the rope passes OVER, the axle is on the inside of the
         * bend: a rope hugs a pulley rather than standing off it. At a stop
         * the rope ENDS on, there is no bend to read, so the reader's own
         * choice of rim decides — which is what `Port.side` has always been
         * for, and the only place it is still used. */
        const side: number[] = stops.map((ref, i) => {
            const rIn = i > 0 ? rough[i - 1] : undefined;
            const rOut = i + 1 < stops.length ? rough[i] : undefined;
            if (rIn && rOut) {
                const turn = rIn.x * rOut.y - rIn.y * rOut.x;
                if (Math.abs(turn) >= 1e-12) return turn > 0 ? 1 : -1;
                /* Straight back the way it came — a dead end rising from a
                 * becket to a top block and returning. There is no bend to read
                 * a hand from, so the rim the reader NAMED decides, measured
                 * against the arriving run, the same rule as an end below. It
                 * used to be a fixed +1, which is a handedness: right in one
                 * orientation of the axes and wrong in the other. */
            }
            const part = partById(w, ref.partId);
            // The rim as NAMED in the path, not its mirror for the leaving run.
            const port = part && portsOf(w, part).find(p => p.id === rope.path[i].portId);
            /* The run AS IT IS WALKED, which for the last stop is the run
             * arriving — not its reverse. `side` is which hand the centre is
             * on going along the rope, so reading it against a direction the
             * rope is not travelled in flips it: a reader joining the LEFT rim
             * got a rope drawn to the right. */
            const run = rIn && rOut ? rIn : (rOut ?? rIn ?? { x: 0, y: 1 });
            // The port says which way it faces in BENCH coordinates; turn
            // that into which side of the run, which is what the tangent
            // solver wants. A port facing along the run itself is no side at
            // all, and takes the same default the old ±1 did.
            const p = { x: run.y, y: -run.x };
            const facing = part && port ? outward(part, port) : { x: 0, y: 0 };
            return dot(facing, p) >= 0 ? 1 : -1;
        });

        // ── 3. the runs, tangent to both ends
        for (let i = 0; i + 1 < stops.length; i++) {
            const a = stops[i], b = stops[i + 1];
            const ra = wheelAt(a), rb = wheelAt(b);
            const ca = heldAt(a, centre(b)), cb = heldAt(b, centre(a));
            const hit = taut(
                { at: ca, radius: ra, side: side[i] },
                { at: cb, radius: rb, side: side[i + 1] },
            );
            /* No such run: a part is inside a wheel, or two wheels overlap.
             * The centres are used instead so the bench still has a geometry
             * to describe, and `diagnose` has the overlap to report. */
            const pa = hit ? hit.pa : ca;
            const pb = hit ? hit.pb : cb;
            const d = sub(pb, pa);
            out.push({
                ropeId: rope.id, index: i, a, b, pa, pb,
                dir: hit ? hit.dir : unit(d), length: hit ? hit.length : len(d), span: 0,
            });
        }
    });

    /* The arc over each stop a run passes OVER rather than ends at. Done in a
     * second pass because the turn is between two runs, and the second one is
     * not built until the first is.
     *
     * Exact now, rather than a drawing: the radius meets a tangent at a right
     * angle, so the angle the rope turns through IS the angle it wraps. */
    for (let i = 0; i + 1 < out.length; i++) {
        if (out[i].ropeId !== out[i + 1].ropeId) continue;
        const stop = out[i].b;
        const part = partById(w, stop.partId);
        const port = part && portsOf(w, part).find(p => p.id === stop.portId);
        if (!part || !port) continue;
        const radius = defOf(part).ropeContact(part as never, port, out[i].pa).wrapRadius;
        if (!radius) continue;
        const turn = Math.acos(Math.max(-1, Math.min(1, dot(out[i].dir, out[i + 1].dir))));
        /* WHERE it touches, not just how far round it goes.
         *
         * The rope lands at the end of the arriving run and leaves at the
         * start of the next, and both are tangent points this now solves for
         * exactly — so the arc is a real arc between two known angles rather
         * than a curve drawn to look right. `sense` is which way round, read
         * from the turn: a rope hugs the wheel, so it goes the short way. */
        const from = Math.atan2(out[i].pb.y - part.at.y, out[i].pb.x - part.at.x);
        const to = Math.atan2(out[i + 1].pa.y - part.at.y, out[i + 1].pa.x - part.at.x);
        const cross = out[i].dir.x * out[i + 1].dir.y - out[i].dir.y * out[i + 1].dir.x;
        out[i].wrap = {
            radius, angle: turn, length: radius * turn,
            from, to, sense: cross >= 0 ? 1 : -1,
        };
    }

    /* Where the rope divides. A run belongs to the same span as the one before
     * it unless the stop between them can hold a tension difference. */
    for (let i = 1; i < out.length; i++) {
        if (out[i].ropeId !== out[i - 1].ropeId) continue;
        const stop = out[i].a;
        const part = partById(w, stop.partId);
        const grips = !!part && !!defOf(part).gripsRope?.(part as never, w);
        out[i].span = out[i - 1].span + (grips ? 1 : 0);
    }
    return out;
}

/** Every rope's spans, with what each one measures now and what it was laid
 *  out as. */
export function spansOf(w: World): Span[] {
    const out: Span[] = [];
    const segs = segmentsOf(w);
    ropesOf(w).forEach(rope => {
        const mine = segs.filter(s => s.ropeId === rope.id);
        if (!mine.length) return;
        const count = mine[mine.length - 1].span + 1;
        for (let k = 0; k < count; k++) {
            const segments = mine.filter(s => s.span === k);
            const now = segments.reduce((a, s) => a + runLength(s), 0);
            out.push({
                ropeId: rope.id,
                index: k,
                id: spanId(rope.id, k),
                segments,
                now,
                /* A rope built by hand carries no per-span lengths, and for the
                 * one-span rope that is every rope today, its total IS the
                 * span's. */
                was: rope.lengths?.[k] ?? (count === 1 ? rope.length : now),
            });
        }
    });
    return out;
}

/**
 * How much more rope there is than the run needs.
 *
 * DERIVED, and that is the point. It used to be a stored surplus, because a
 * length typed in would be overwritten the next time a part was dragged — so
 * the one quantity that survived a moving baseline was the difference from it.
 * With `length` authored there is no moving baseline, and the difference is
 * simply arithmetic on two numbers the bench already has. One truth.
 *
 * Never negative: a run longer than its rope is not slack, it is a rope being
 * stretched, which `diagnose` reports as a fault rather than a number.
 */
export const slackOf = (w: World, rope: Rope): number =>
    Math.max(0, +spansOf(w).filter(s => s.ropeId === rope.id)
        .reduce((a, s) => a + (s.was - s.now), 0).toFixed(6));

/** What each span of a rope measures where it is laid out. */
export const measureSpans = (w: World, rope: Rope): number[] =>
    spansOf({ ...w, links: [{ ...rope, lengths: undefined }] }).map(s => s.now);

/**
 * How much rope this run takes: the straight part, AND the arc it ends on.
 *
 * The arc is rope. It was not, while a wheel had no size — a rope meeting a
 * point loses nothing going round it — and leaving it out then was the only
 * consistent thing to do. With the rope tangent to a real rim the wrap is
 * `r·θ` of actual rope, and not counting it would make a rope's length change
 * as the bench moved, which is the one thing a rope's length may never do.
 */
export const runLength = (seg: Segment): number => seg.length + (seg.wrap?.length ?? 0);

/** How long a rope is where it is laid out — what `Rope.length` is set from. */
export function measureRope(w: World, rope: Rope): number {
    return segmentsOf({ ...w, links: [rope] }).reduce((s, seg) => s + runLength(seg), 0);
}

/**
 * The parts, welded into bodies by the bonds.
 *
 * Union-find rather than a walk, because a student can bond A to B and then B
 * to C and the result must not depend on the order they did it in.
 */
export function bodiesOf(w: World): Body[] {
    const parent = new Map<string, string>();
    w.parts.forEach(p => parent.set(p.id, p.id));
    const find = (a: string): string => {
        let r = a;
        while (parent.get(r) !== r) r = parent.get(r) as string;
        while (parent.get(a) !== r) { const nx = parent.get(a) as string; parent.set(a, r); a = nx; }
        return r;
    };
    bondsOf(w).forEach(b => {
        if (!parent.has(b.hangs.partId) || !parent.has(b.holds.partId)) return;
        const x = find(b.hangs.partId), y = find(b.holds.partId);
        if (x !== y) parent.set(x, y);
    });

    const groups = new Map<string, string[]>();
    w.parts.forEach(p => {
        const r = find(p.id);
        const list = groups.get(r);
        if (list) list.push(p.id); else groups.set(r, [p.id]);
    });

    const out: Body[] = [];
    groups.forEach((partIds, id) => {
        const parts = partIds.map(i => partById(w, i) as Part);
        const freedoms = parts.map(p => freedomOf(w, p));
        /* A body is as free as its LEAST free part. One anchor in a group pins
         * the whole group, which is exactly what bolting something to the
         * ceiling means. */
        const pinned = freedoms.some(f => f.axes.length === 0);
        /* Resting on a face, or running on a rail, is the more specific
         * statement — so it wins over whatever else in the group merely hangs. */
        const seated = parts.findIndex(p => supportOf(w, p));
        const railed = freedoms.findIndex(f => f.track && f.axes.length);
        out.push({
            id,
            partIds,
            mass: parts.reduce((s, p) => s + defOf(p).mass(p as never), 0),
            inertia: parts.reduce((s, p) => s + (defOf(p).inertia?.(p as never) ?? 0), 0),
            axes: pinned ? []
                : seated >= 0 ? freedoms[seated].axes
                : railed >= 0 ? freedoms[railed].axes
                : (freedoms.find(f => f.axes.length)?.axes ?? [DOWN]),
            spins: freedoms.some(f => f.spins),
            q: [],
            v: [],
            theta: 0,
            omega: 0,
        });
    });
    return out;
}

/** Which body each part is in. */
export function bodyIndex(bodies: Body[]): Map<string, Body> {
    const m = new Map<string, Body>();
    bodies.forEach(b => b.partIds.forEach(id => m.set(id, b)));
    return m;
}

/** A body with somewhere to go. A pinned one is not solved for. */
export const isFree = (b: Body): boolean => b.axes.length > 0;
